import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

// Services
import useAccommodationService from '../../../../../../../services/accommodationService';

// Components
import InputField from '../../../../../../atoms/InputField/InputField';
import TagsInput from '../../../../../../molecules/TagsInput/TagsInput';
import SkeletonLoader from '../../../../../../molecules/SkeletonLoader/SkeletonLoader';
import SelectField from '../../../../../../atoms/SelectField/SelectField';

function General({ formik }) {
  const { t } = useTranslation();

  const { getAccommodationTypes } = useAccommodationService();
  const { data: types, isLoading: isTypesLoading } = useQuery('getTypes', getAccommodationTypes);

  const specOptions = [
    { label: t('accommodations.marketing.form.isAnAdultMandatory'), value: 'isAnAdultMandatory' },
    { label: t('accommodations.marketing.form.isForChildrenOnly'), value: 'isForChildrenOnly' },
  ];

  const getInput = useCallback((name, type, min) => (
    <div className="m-b-15">
      <InputField
        type={type || 'text'}
        id={`accommodation_marketing_${name}`}
        name={name}
        value={formik.values[name]}
        label={t(`accommodations.marketing.form.${name}`)}
        formik={{
          handleChange: formik.handleChange,
          handleBlur: formik.handleBlur,
          setFieldValue: formik.setFieldValue,
        }}
        error={formik.errors?.[name] && formik.touched?.[name] ? formik.errors[name] : null}
        isRequired
        full
        minNumberValue={min}
      />
    </div>
  ), [formik]);

  const getInputTags = useCallback((name, options, isLoading) => (
    isLoading ? (
      <div className="d-flex f-wrap">
        <SkeletonLoader width={100} height={40} className="m-r-10" radius={40} />
        <SkeletonLoader width={100} height={40} className="m-r-10" radius={40} />
        <SkeletonLoader width={100} height={40} className="m-r-10" radius={40} />
      </div>
    ) : (
      options?.length ? (
        <TagsInput
          name={name}
          options={options}
          selected={formik.values[name]}
          setFieldValue={formik.setFieldValue}
          setFieldTouched={formik.setFieldTouched}
          error={formik.errors?.[name] && formik.touched?.[name] ? formik.errors[name] : null}
        />
      ) : null
    )
  ), [formik, isTypesLoading]);

  const getSelectField = useCallback((name) => (
    <SelectField
      id={name}
      name={name}
      options={Array.from(Array(24).keys()).map((_, index) => ({ label: `${index}:00`, value: index }))}
      label={t(`accommodations.marketing.form.${name}`)}
      error={formik.errors?.[name] && formik.touched?.[name] ? formik.errors[name] : null}
      setFieldTouched={formik.setFieldTouched}
      setFieldValue={formik.setFieldValue}
      value={formik.values[name]}
    />
  ), [formik]);

  return (
    <div>
      <h3 className="fs-20 m-b-25">{t('accommodations.marketing.sub.informations')}</h3>

      <div className="input_max">
        {getInput('name', null, null)}
      </div>
      <div className="input_max small">
        {getInput('size', 'number', 0)}
      </div>

      <hr className="m-t-30 m-b-40" />

      <h3 className="fs-20 m-b-15">
        {t('accommodations.marketing.sub.type')}
        <span className="c-primary-500 m-l-5 fs-18">*</span>
      </h3>

      <p className="m-b-25 fs-14 fw-300">{t('accommodations.marketing.type.warning')}</p>

      <div className="input_max">
        {getInputTags(
          'type',
          types?.data?.accommodationTypes?.map(
            (option) => ({ label: t(`accommodations.types.${option}`), value: option }),
          ),
          isTypesLoading,
        )}
      </div>

      <hr className="m-t-30 m-b-40" />

      <h3 className="fs-20 m-b-25">
        {t('accommodations.marketing.sub.capacity')}
      </h3>

      <div className="input_max small">
        {getInput('capacity', 'number', 1)}
      </div>

      <hr className="m-t-30 m-b-40" />

      <h3 className="fs-20 m-b-25">
        {t('accommodations.marketing.sub.spec')}
        <span className="c-primary-500 m-l-5 fs-18">*</span>
      </h3>

      <div className="input_max">
        {getInputTags(
          'spec',
          specOptions,
          false,
        )}
      </div>

      <hr className="m-t-30 m-b-40" />

      <h3 className="fs-20 m-b-25">
        {t('accommodations.marketing.sub.times')}
      </h3>

      <div className="d-flex mobile-d-col">
        <div className="input_max small m-r-20 mobile-m-r-0 mobile-m-b-10">
          {getSelectField('checkInFromHour')}
        </div>
        <div className="input_max small">
          {getSelectField('checkOutUntilHour')}
        </div>
      </div>
    </div>
  );
}

General.propTypes = {
  formik: PropTypes.shape({
    values: PropTypes.shape({
      checkInFromHour: PropTypes.shape({}),
    }),
    handleChange: PropTypes.func,
    handleBlur: PropTypes.func,
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    errors: PropTypes.shape({
      checkInFromHour: PropTypes.string,
    }),
    touched: PropTypes.shape({
      checkInFromHour: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({})]),
    }),
  }).isRequired,
};

export default General;
